import { buildTitle } from "@/lib/seo-quality";
import { clampDescription } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { PageHeader } from "./fordon";
import { getPrisTier, PRIS_TIERS } from "@/data/husbil-pris-ar";
import { getPrisDjup } from "@/data/husbil-pris-djup";
import { assessPrisTier } from "@/lib/seo-families/pris";
import { robotsMeta } from "@/lib/seo-quality";

export const Route = createFileRoute("/husbil-till-salu-under/$pris")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const tier = getPrisTier(params.pris);
    if (!tier) throw notFound();
    return { tier, djup: getPrisDjup(tier.slug) ?? null, quality: assessPrisTier(tier) };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { tier, djup, quality } = loaderData;
    const url = `https://www.akhusbilfritid.se/husbil-till-salu-under/${tier.slug}`;
    const title = buildTitle(`Husbil under ${tier.label} kr — så mycket får du 2026`);
    const desc = `Husbil under ${tier.label} kr: vad du realistiskt får, vilka modeller som passar, vad du bör undvika. Aktuellt lager hos AK Husbil i Kalix, Norrbotten.`;
    return {
      meta: [
        { title },
        { name: "description", content: clampDescription(desc) },
        ...robotsMeta(quality),
        { property: "og:title", content: title },
        { property: "og:description", content: clampDescription(desc) },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: `Vad kan man förvänta sig av husbil under ${tier.label} kr?`,
                acceptedAnswer: { "@type": "Answer", text: tier.reality },
              },
              {
                "@type": "Question",
                name: `Vilka modeller är typiska i prisklassen under ${tier.label} kr?`,
                acceptedAnswer: { "@type": "Answer", text: tier.typical },
              },
              {
                "@type": "Question",
                name: `Vad ska man undvika i denna prisklass?`,
                acceptedAnswer: { "@type": "Answer", text: tier.avoid.join(". ") + "." },
              },
              ...(djup
                ? [
                    {
                      "@type": "Question",
                      name: `Vad kostar en husbil under ${tier.label} kr att äga per år?`,
                      acceptedAnswer: { "@type": "Answer", text: djup.driftkostnad },
                    },
                    ...djup.faq.map((f) => ({
                      "@type": "Question",
                      name: f.q,
                      acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                  ]
                : []),
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="max-w-2xl mx-auto p-8 text-center">
      <h1 className="text-2xl font-bold mb-4">Prisklassen finns inte</h1>
      <Link to="/husbilar-till-salu" className="text-primary underline">Se alla husbilar</Link>
    </div>
  ),
  component: PrisPage,
});

function PrisPage() {
  const { tier, djup } = Route.useLoaderData();
  return (
    <>
      <PageHeader
        label="Prisguide"
        title={<>Husbil under {tier.label} kr</>}
        sub="Vad du realistiskt får för pengarna — och vad du bör undvika"
      />
      <main className="max-w-4xl mx-auto px-4 py-10 space-y-10">
        <p className="text-lg leading-relaxed">{tier.intro}</p>

        <section className="rounded-xl border p-6 bg-muted/40">
          <h2 className="text-xl font-bold mb-3">{tier.what}</h2>
          <p>{tier.reality}</p>
        </section>

        <section className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold mb-3 flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" /> Passar för
            </h3>
            <ul className="space-y-2">
              {tier.goodFor.map((g: string) => (
                <li key={g} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-1" /><span>{g}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-bold mb-3 flex items-center gap-2 text-orange-700">
              <XCircle className="w-5 h-5" /> Passar inte för
            </h3>
            <ul className="space-y-2">
              {tier.avoid.map((a: string) => (
                <li key={a} className="flex gap-2"><XCircle className="w-4 h-4 text-orange-600 shrink-0 mt-1" /><span>{a}</span></li>
              ))}
            </ul>
          </div>
        </section>

        {djup && (
          <>
            <section>
              <h2 className="text-2xl font-bold mb-4">Vad kostar det att äga? (under {tier.label} kr)</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-lg border p-5">
                  <div className="text-sm text-muted-foreground mb-1">Fordonsskatt</div>
                  <div className="font-semibold">{djup.skatt}</div>
                </div>
                <div className="rounded-lg border p-5">
                  <div className="text-sm text-muted-foreground mb-1">Försäkring</div>
                  <div className="font-semibold">{djup.forsakring}</div>
                </div>
              </div>
              <p className="mt-4 leading-relaxed">{djup.driftkostnad}</p>
            </section>

            <section className="rounded-xl border p-6 bg-muted/30">
              <h2 className="text-xl font-bold mb-2">Finansiering i denna prisklass</h2>
              <p className="leading-relaxed">{djup.finansiering}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-3">Förhandlingsläget</h2>
              <p className="leading-relaxed">{djup.forhandling}</p>
              <h3 className="font-bold mt-5 mb-2">Värt att satsa på efter köpet</h3>
              <p className="leading-relaxed text-muted-foreground">{djup.eftersatsning}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Checklista vid visning</h2>
              <ul className="space-y-2">
                {djup.checklista.map((c: string) => (
                  <li key={c} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-1" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Vanligaste misstagen i prisklassen</h2>
              <ul className="space-y-2">
                {djup.vanligaMisstag.map((m: string) => (
                  <li key={m} className="flex gap-2">
                    <XCircle className="w-4 h-4 text-orange-600 shrink-0 mt-1" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">Vanliga frågor</h2>
              <div className="space-y-4">
                {djup.faq.map((f: { q: string; a: string }) => (
                  <div key={f.q} className="rounded-lg border p-5">
                    <h3 className="font-bold mb-2">{f.q}</h3>
                    <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        <section>
          <h2 className="text-2xl font-bold mb-3">Typiska modeller under {tier.label} kr</h2>
          <p className="text-muted-foreground">{tier.typical}</p>
        </section>

        <section className="grid sm:grid-cols-3 gap-3">
          {PRIS_TIERS.filter((p) => p.slug !== tier.slug).slice(0, 6).map((p) => (
            <Link
              key={p.slug}
              to="/husbil-till-salu-under/$pris"
              params={{ pris: p.slug }}
              className="rounded-lg border p-4 hover:border-primary hover:bg-muted/40"
            >
              <div className="text-xs text-muted-foreground">Se prisklass</div>
              <div className="font-semibold">Under {p.label} kr →</div>
            </Link>
          ))}
        </section>

        <section className="rounded-xl border-2 border-primary/20 bg-muted/40 p-6">
          <h2 className="text-xl font-bold mb-2">Aktuella husbilar under {tier.label} kr</h2>
          <p className="text-muted-foreground mb-4">
            Sortera på pris i lagerlistan och sätt max till {tier.label.replace(/\s/g, "")}.
          </p>
          <Link to="/husbilar-till-salu" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90">
            Se alla husbilar <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Osäker på vilken prisklass som passar?</h2>
          <p className="mb-6 opacity-90">Ring oss — vi går igenom behov och budget utan säljtryck.</p>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
            <Phone className="w-4 h-4" /> 070-862 97 43
          </a>
        </section>
      </main>
    </>
  );
}
